/**
 * Handles common errors by displaying notifications to the user based on the error type.
 * 
 * @param {Error | any} error - The error object that can be of various types, typically including a status code and message.
 * 
 * Error Handling:
 * - If the error has a status code of 401 (Unauthorized), a notification is shown, and the user is redirected to the login page after 5 seconds.
 * - Server errors (status codes 500 and above) trigger a "Server Error" notification.
 * - Client errors (status codes 400-499) display a notification with the specific status code and message.
 * - All other errors are handled by showing a generic error notification.
 * 
 * Logging:
 * - Detailed error information is logged to the console for debugging purposes.
 */

import { notifications } from "@mantine/notifications";

export function commonErrorHandler(error: Error | any) {
    const errorMessage = error?.response?.data?.message || error?.message || 'An unknown error occurred';
    console.error('Error Details:', error);
    console.error('Error Message:', errorMessage);
    const errorStatusCode = error.statusCode || error.response?.status;

    // Specific handling for 403 Unauthorized errors
    if (errorStatusCode === 403) {
        notifications.show({
            title: 'Session Expired',
            message: 'Your session has expired. Redirecting to the login page...',
            color: 'red',
            autoClose: 5000,
            position: 'top-center',
        });
        return; // Exit early since we've already handled the error
    }

    if (errorStatusCode === 429) {
        notifications.show({
            title: 'Too many requests',
            message: 'Too many requests. Please try again later.',
            color: 'red',
            position: 'top-center',
        });
        return;
    }

    // Handle network or server errors
    if (errorStatusCode >= 500) {
        notifications.show({
            title: 'Something went wrong',
            message: errorMessage,
            color: 'red',
            position: 'top-center',
        });
    } else if (errorStatusCode >= 400) {
        notifications.show({
            title: `Message`,
            message: errorMessage,
            color: 'red',
            position: 'top-center',
        });
    } else {
        // Default error handler for other cases
        notifications.show({
            title: 'Message',
            message: errorMessage,
            color: 'red',
            position: 'top-center',
        });
    }

    // Log detailed error information to the console
    console.error('Error Details:', error);
}
